import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import moment from 'moment';
import { axiosGetCall } from 'src/services/apisCall';
import { renderErrorMessage } from 'src/lib/toastMessage';
import SmsAdminSettingDetailsModal from './SmsAdminSettingDetailsModal';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'In Progress' },
  { value: '2', label: 'Processed' },
  { value: '-1', label: 'Failed' },
];

export default function SmsAdminSettingTable() {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selected item for details modal
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = page * rowsPerPage;
      const params = {
        skip,
        limit: rowsPerPage,
        page: page + 1,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search.trim()) {
        params.campaignId = search.trim();
      }

      const response = await axiosGetCall('getSmsAdminSettings', params);
      const list = response?.list || response?.data || [];
      const total = response?.totalCount ?? list.length;

      setData(list);
      setTotalCount(total);
    } catch (err) {
      const msg = err?.message || 'Failed to fetch SMS admin settings';
      setError(msg);
      renderErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPage(0);
  };

  const getActionChips = (row) => {
    const chips = [];
    if (row.D_required) {
      chips.push(
        <Chip
          key="D"
          label={`Delivered: ${row.D_required.count}${row.D_required.countMode === 'percent' ? '%' : ''}`}
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    if (row.S_required) {
      chips.push(
        <Chip
          key="S"
          label={`Submitted: ${row.S_required.count}${row.S_required.countMode === 'percent' ? '%' : ''}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }
    if (row.F_required) {
      chips.push(
        <Chip
          key="F"
          label={`Failed: ${row.F_required.count}${row.F_required.countMode === 'percent' ? '%' : ''}`}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    }
    if (row.P_required) {
      chips.push(
        <Chip
          key="P"
          label={`Pending: ${row.P_required.count}${row.P_required.countMode === 'percent' ? '%' : ''}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    }
    if (row.DateTime_required) {
      chips.push(<Chip key="DT" label="Date/Time" size="small" color="info" variant="outlined" />);
    }
    return chips;
  };

  const renderStatusChip = (status) => {
    switch (status) {
      case 0:
        return <Chip label="Pending" size="small" color="warning" variant="soft" />;
      case 1:
        return <Chip label="In Progress" size="small" color="info" variant="soft" />;
      case 2:
        return <Chip label="Processed" size="small" color="success" variant="soft" />;
      case -1:
        return <Chip label="Failed" size="small" color="error" variant="soft" />;
      default:
        return <Chip label={String(status)} size="small" variant="soft" />;
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      {/* Header & Controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={1}>
          <TextField
            size="small"
            placeholder="Search by Campaign ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(search || statusFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              startIcon={<FilterListOffIcon />}
            >
              Reset
            </Button>
          )}
        </Stack>

        <Tooltip title="Refresh">
          <IconButton onClick={fetchSettings} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Seq ID</TableCell>
              <TableCell>Campaign IDs</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell align="center">Update Existing</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell align="center">View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No SMS admin settings found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>#{row.seq_id ?? '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(row.campaignIds || []).slice(0, 3).map((id) => (
                        <Chip key={id} label={id} size="small" variant="outlined" />
                      ))}
                      {(row.campaignIds || []).length > 3 && (
                        <Chip label={`+${row.campaignIds.length - 3}`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.selectedUserId || row.userId || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {getActionChips(row)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {row.updateExistingRecords ? (
                      <Chip label="Yes" size="small" color="primary" variant="soft" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip label="No (Cut=1)" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.requested_by || '—'}</Typography>
                  </TableCell>
                  <TableCell>{renderStatusChip(row.status)}</TableCell>
                  <TableCell>
                    {row.create_date
                      ? moment.unix(row.create_date).format('DD MMM YYYY, hh:mm A')
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSetting(row);
                          setDetailsOpen(true);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Details Modal */}
      <SmsAdminSettingDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedSetting(null);
        }}
        setting={selectedSetting}
      />
    </Card>
  );
}
